import { setContext } from '@apollo/client/link/context'
import { initializeApollo } from '@jusbrasil-web/shared-apollo-client'

import { APOLLO_INTERNAL_CSR_URI, APOLLO_INTERNAL_STATE } from '../constants'
import { AppPropsWithApollo, WithApollo } from '../types'

const noop = () => undefined

export const withApollo: WithApollo = (ctx, options = {}) => {
  const { initialState, clientOverrides = {} } = options
  const client = initializeApollo(initialState, ctx.req, clientOverrides)

  // SSR only — `ctx.req` exists when withApollo runs in `getServerSideProps`.
  // CSR (`useApollo(state)`) does not pass here; on the browser, auth is Kong
  // B2B's job (browser → Kong injects Authorization), Web SSR does nothing.
  //
  // The auth pipeline (`@jusbrasil/auth-strategies` + `jose`) is server-only and
  // pulled via a dynamic `import('./server-auth')`: webpack emits it as its own
  // chunk that the browser never fetches (this branch runs only on the server),
  // so it stays out of the client first-load bundle. See `./server-auth` for the
  // strategy chain + threat model.
  //
  // `setContext` is async (auth is async — `jose.jwtVerify` is async-only); Apollo
  // awaits it before each SSR query. `withApollo` itself stays sync. The shared
  // `headersPromise` is a natural singleflight across all SSR queries of this
  // request: the chunk loads and the chain runs once, every query awaits the same
  // result.
  if (ctx.req) {
    const req = ctx.req
    const headersPromise = import('./server-auth').then(({ resolveAuthHeaders }) =>
      resolveAuthHeaders({ headers: req.headers })
    )
    const authLink = setContext(async (_, prevContext) => {
      const headers = await headersPromise
      if (!headers) return prevContext
      const prevHeaders = (prevContext as { headers?: Record<string, string> }).headers ?? {}
      return {
        ...prevContext,
        headers: { ...prevHeaders, ...headers },
      }
    })
    client.setLink(authLink.concat(client.link))
  }

  let usageWarningTimer: ReturnType<typeof setTimeout> | undefined

  if (process.env.NODE_ENV !== 'production') {
    const error = new Error(
      '[core-apollo] withApollo() was called but withApolloProps() was never invoked. ' +
        'This means the Apollo cache state will not be passed to the client, causing hydration issues. ' +
        'Make sure to spread ...withApolloProps() in your getServerSideProps return value.'
    )
    usageWarningTimer = setTimeout(() => {
      console.warn(error)
    }, 5_000)
    usageWarningTimer.unref?.()
  }

  return {
    client,
    withApolloProps() {
      clearTimeout(usageWarningTimer)
      const clientCacheExtract = client.cache.extract()
      client
        .clearStore()
        .then(noop)
        .catch((rejectionReason) =>
          console.error(
            'Error fire-and-forget clearing Apollo client store after request',
            rejectionReason
          )
        )

      const props: AppPropsWithApollo = {
        [APOLLO_INTERNAL_STATE]: clientCacheExtract,
      }

      if (clientOverrides.csrUri) {
        props[APOLLO_INTERNAL_CSR_URI] = clientOverrides.csrUri
      }

      return props
    },
  }
}
